import { useAuth } from '@/lib/auth.context'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { LogOut, ChevronDown, Settings, Sun, Moon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useMerchant } from '@/lib/hooks/useMerchant'
import { useMerchantProfile } from '@/hooks/useMerchantProfile'
import { useTheme } from '@/lib/theme-context'

function ThemeToggle() {
  const { theme, setTheme } = useTheme()

  return (
    <Button variant="outline" className="hover:bg-transparent" size="icon" onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}>
      <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
      <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
      <span className="sr-only">Toggle theme</span>
    </Button>
  )
}

export function Header() {
  const { user, status, logout } = useAuth()
  const { merchantId } = useMerchant()
  const { data: merchantProfile, isLoading: merchantLoading } = useMerchantProfile(merchantId)
  const navigate = useNavigate()

  const handleLogout = async () => {
    await logout(true)
  }

  if (status === 'loading') return <div className="h-16" />

  if (!user) {
    return null
  }

  const getInitials = (name: string) => {
    const nameParts = name.split(' ').filter(Boolean)
    if (nameParts.length === 0) return '??'
    if (nameParts.length === 1) return nameParts[0].substring(0, 2).toUpperCase()
    const firstInitial = nameParts[0].charAt(0)
    const lastInitial = nameParts[nameParts.length - 1].charAt(0)
    return `${firstInitial}${lastInitial}`.toUpperCase()
  }

  const firstName = user.user_metadata?.first_name
  const lastName = user.user_metadata?.last_name
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || user.email || 'User'
  const initials = getInitials(fullName)
  const email = user.email

  return (
    <header className="h-header bg-card border-b border-border flex items-center justify-between px-6">
      {/* Left Side — Merchant Name */}
      <div className="flex items-center">
        {merchantLoading ? (
          <Skeleton className="h-6 w-48" />
        ) : (
          <h2 className="text-lg font-semibold text-foreground">
            {merchantProfile?.merchantName || 'Merchant Portal'}
          </h2>
        )}
      </div>

      {/* Right Side — User Menu */}
      <div className="flex items-center gap-4">
        <ThemeToggle />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-3 px-3 py-2 h-auto">
              <div className="h-8 w-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-medium text-primary-foreground">{initials}</span>
              </div>
              <div className="hidden md:flex flex-col items-start">
                <span className="text-sm font-medium leading-tight">{fullName}</span>
                <span className="text-xs text-muted-foreground leading-tight">{email}</span>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/profile')}>
              <Settings className="mr-2 h-4 w-4" />
              <span>Settings</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              <span>Sign out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
